import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Button } from './Button';

interface MissingScore {
    cluster: string;
    subcategories: string[];
}

interface CompletionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    missingFields: string[];
    missingScores: MissingScore[];
    isValidating: boolean;
}

export function CompletionModal({
    isOpen,
    onClose,
    onConfirm,
    missingFields,
    missingScores,
    isValidating
}: CompletionModalProps) {
    const t = useTranslations('completionModal');

    if (!isOpen) return null;

    const hasMissingData = missingFields.length > 0 || missingScores.length > 0;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.98, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98, y: 10 }}
                    className="bg-white/80 backdrop-blur-md rounded-xs shadow-2xl border border-white/20 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="p-6 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-black flex items-center justify-center text-white rounded-xs">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-black uppercase tracking-tight">{t('title')}</h3>
                                <p className="text-sm text-slate-500">{t('subtitle')}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-black transition-colors p-2 cursor-pointer"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-8">
                        {isValidating ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-black mb-6" />
                                <p className="text-black font-medium tracking-wide uppercase text-sm">{t('validating')}</p>
                            </div>
                        ) : (
                            <>
                                {!hasMissingData ? (
                                    <div className="p-8 text-center">
                                        <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                            <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <h4 className="text-black font-bold text-xl mb-3">{t('successTitle')}</h4>
                                        <p className="text-slate-600 max-w-md mx-auto">{t('successDescription')}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-8">
                                        <div className="bg-rose-50 border border-rose-100 rounded-xs p-5 flex gap-4 text-rose-900 shadow-sm">
                                            <svg className="w-6 h-6 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            <div className="text-sm">
                                                <p className="font-bold uppercase tracking-tight mb-1">{t('missingTitle')}</p>
                                                <p className="text-rose-800/80">{t('missingDescription')}</p>
                                            </div>
                                        </div>

                                        {missingFields.length > 0 && (
                                            <div>
                                                <div className="flex items-center gap-2 mb-4">
                                                    <div className="h-4 w-1 bg-black" />
                                                    <h4 className="text-sm font-bold text-black uppercase tracking-widest">{t('missingDetails')}</h4>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    {missingFields.map((field, idx) => (
                                                        <div key={idx} className="flex items-center gap-3 text-sm text-slate-800 bg-slate-50 px-4 py-3 rounded-xs border border-slate-100">
                                                            <div className="w-2 h-2 bg-rose-500" />
                                                            {t(`fields.${field}` as any, { defaultValue: field })}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {missingScores.length > 0 && (
                                            <div>
                                                <div className="flex items-center gap-2 mb-4">
                                                    <div className="h-4 w-1 bg-black" />
                                                    <h4 className="text-sm font-bold text-black uppercase tracking-widest">{t('incompleteScoring')}</h4>
                                                </div>
                                                <div className="space-y-4">
                                                    {missingScores.map((ms, idx) => (
                                                        <div key={idx} className="bg-slate-50 rounded-xs p-5 border border-slate-200">
                                                            <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-200">
                                                                <h5 className="font-bold text-black text-sm uppercase">{ms.cluster}</h5>
                                                                <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-xs border border-rose-100 uppercase tracking-tighter">
                                                                    {t('missingCount', { count: ms.subcategories.length })}
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-wrap gap-2">
                                                                {ms.subcategories.map((sub, sIdx) => (
                                                                    <span key={sIdx} className="text-[11px] font-medium bg-white text-slate-600 px-3 py-1.5 rounded-xs border border-slate-200 shadow-sm">
                                                                        {sub}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-6 flex items-center justify-end gap-4 bg-transparent border-t border-white/10">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            disabled={isValidating}
                            className="w-auto px-6 whitespace-nowrap"
                        >
                            {t('continueEditing')}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={onConfirm}
                            disabled={isValidating || hasMissingData}
                            className="w-auto px-6 whitespace-nowrap shadow-xl shadow-slate-200 disabled:shadow-none"
                            title={hasMissingData ? t('finalizeDisabledTitle') : ""}
                        >
                            {t('finalize')}
                        </Button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
